import { useState, useEffect, useRef } from "react";
import { XAxis, YAxis, Tooltip, ResponsiveContainer, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, CartesianGrid, AreaChart, Area } from "recharts";

const C = {
  bg: "#0D0A1E", surface: "#121030", surfaceHover: "#1A1838", card: "#1E1B4B",
  border: "#2D2A5E", accent: "#FF6B6B", accentSoft: "#FF6B6B22", accentGlow: "#FF6B6B44",
  purple: "#A78BFA", purpleSoft: "#A78BFA22", green: "#34D399", greenSoft: "#34D39922",
  amber: "#FBBF24", amberSoft: "#FBBF2422", blue: "#60A5FA", blueSoft: "#60A5FA22",
  rose: "#FB7185", roseSoft: "#FB718522",
  text: "#F0F0F5", textMuted: "#9898B8", textDim: "#55557A",
};

const STAGES = {
  talking: { label: "Talking", color: C.blue, bg: C.blueSoft, emoji: "💬" },
  first_date: { label: "First Date", color: C.purple, bg: C.purpleSoft, emoji: "🍷" },
  dating: { label: "Dating", color: C.green, bg: C.greenSoft, emoji: "💚" },
  exclusive: { label: "Exclusive", color: C.accent, bg: C.accentSoft, emoji: "❤️" },
  ended: { label: "Ended", color: C.textDim, bg: "#55557022", emoji: "👻" },
};

const SAMPLE_DATES = [
  { id: 1, name: "Alex R.", stage: "dating", platform: "Hinge", mood: "🔥", lastUpdate: "3rd date was perfect — planning a weekend trip upstate", vibe: 8, effort: 9, calm: 7, values: 8, chemistry: 9, avatar: "🧔", dateCount: 3, met: "Feb 1" },
  { id: 2, name: "Dev M.", stage: "talking", platform: "Bumble", mood: "🤔", lastUpdate: "Matched 8 days ago, no date planned — situationship risk", vibe: 6, effort: 5, calm: 8, values: 7, chemistry: 5, avatar: "👨", dateCount: 0, met: "Feb 5" },
  { id: 3, name: "James K.", stage: "ended", platform: "Hinge", mood: "😬", lastUpdate: "Love bombing flagged by date 2 — pattern broken", vibe: 9, effort: 4, calm: 3, values: 5, chemistry: 10, avatar: "👨‍🦱", dateCount: 4, met: "Jan 10" },
  { id: 4, name: "Rohan S.", stage: "first_date", platform: "IRL", mood: "💚", lastUpdate: "Met at Garam Masala — coffee Saturday morning", vibe: 7, effort: 8, calm: 9, values: 9, chemistry: 6, avatar: "🧑", dateCount: 0, met: "Feb 4" },
  { id: 5, name: "Chris T.", stage: "ended", platform: "Tinder", mood: "😬", lastUpdate: "Ghosted after date 2 — classic low-effort pattern", vibe: 4, effort: 3, calm: 6, values: 4, chemistry: 7, avatar: "👱", dateCount: 2, met: "Jan 15" },
  { id: 6, name: "Nikhil P.", stage: "exclusive", platform: "Hinge", mood: "🔥", lastUpdate: "6 months in — met his parents last week", vibe: 9, effort: 9, calm: 9, values: 9, chemistry: 8, avatar: "🧑‍🦰", dateCount: 24, met: "Aug 2025" },
];

function useIsMobile(breakpoint = 640) {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= breakpoint);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= breakpoint);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, [breakpoint]);
  return isMobile;
}

function Badge({ children, color, bg }) {
  return <span style={{ display: "inline-block", padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 600, letterSpacing: 0.5, color, background: bg, textTransform: "uppercase" }}>{children}</span>;
}

function StatCard({ label, value, sublabel, color }) {
  return (
    <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 16, padding: "20px 24px", flex: 1, minWidth: 140 }}>
      <div style={{ fontSize: 11, color: C.textMuted, textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>{label}</div>
      <div style={{ fontSize: 32, fontWeight: 700, color: color || C.text, lineHeight: 1 }}>{value}</div>
      {sublabel && <div style={{ fontSize: 12, color: C.textMuted, marginTop: 6 }}>{sublabel}</div>}
    </div>
  );
}

function Nav({ active, setActive }) {
  const mob = useIsMobile();
  const tabs = [
    { id: "landing", label: "Home", icon: "🏠" },
    { id: "board", label: "Board", icon: "📋" },
    { id: "insights", label: "Insights", icon: "📊" },
    { id: "wrapped", label: "Wrapped", icon: "🎁" },
    { id: "coach", label: "Wingman", icon: "🧠" },
  ];

  if (mob) {
    return (
      <>
        {/* Minimal top bar on mobile */}
        <div style={{ position: "sticky", top: 0, zIndex: 100, background: C.bg + "EE", backdropFilter: "blur(20px)", borderBottom: `1px solid ${C.border}`, padding: "12px 16px", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ fontSize: 16, fontWeight: 800, color: C.accent, fontFamily: "'Georgia', serif", cursor: "pointer" }} onClick={() => setActive("landing")}>❤️‍🔥 dating second brain</div>
        </div>
        {/* Fixed bottom tab bar */}
        <nav style={{ position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 100, background: C.bg + "F5", backdropFilter: "blur(20px)", borderTop: `1px solid ${C.border}`, display: "flex", justifyContent: "space-around", paddingBottom: "env(safe-area-inset-bottom, 8px)" }}>
          {tabs.map(t => (
            <button key={t.id} onClick={() => setActive(t.id)} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2, padding: "8px 4px", background: "none", border: "none", cursor: "pointer", color: active === t.id ? C.accent : C.textMuted, fontSize: 18, fontFamily: "inherit", flex: 1, transition: "color 0.2s" }}>
              <span>{t.icon}</span>
              <span style={{ fontSize: 9, fontWeight: active === t.id ? 700 : 500, letterSpacing: 0.3 }}>{t.label}</span>
            </button>
          ))}
        </nav>
      </>
    );
  }

  return (
    <nav style={{ position: "sticky", top: 0, zIndex: 100, background: C.bg + "EE", backdropFilter: "blur(20px)", borderBottom: `1px solid ${C.border}`, padding: "0 24px", display: "flex", alignItems: "center" }}>
      <div style={{ fontSize: 18, fontWeight: 800, color: C.accent, marginRight: 24, padding: "16px 0", fontFamily: "'Georgia', serif", cursor: "pointer", whiteSpace: "nowrap" }} onClick={() => setActive("landing")}>❤️‍🔥 dsb</div>
      <div style={{ display: "flex", gap: 2, overflowX: "auto", flex: 1 }}>
        {tabs.map(t => (
          <button key={t.id} onClick={() => setActive(t.id)} style={{ padding: "16px 12px", background: "none", border: "none", cursor: "pointer", color: active === t.id ? C.text : C.textMuted, fontSize: 13, fontWeight: active === t.id ? 600 : 400, borderBottom: active === t.id ? `2px solid ${C.accent}` : "2px solid transparent", fontFamily: "inherit", whiteSpace: "nowrap" }}>{t.icon} {t.label}</button>
        ))}
      </div>
    </nav>
  );
}

// ============ LANDING ============

function LandingPage({ setActive }) {
  const mob = useIsMobile();

  // CSS phone mockup showing mini board cards
  const PhoneMockup = () => (
    <div style={{ display: "flex", justifyContent: "center", marginTop: mob ? 40 : 56 }}>
      <div style={{ width: mob ? 220 : 260, background: "#0D0A1E", borderRadius: 36, border: `2px solid ${C.border}`, boxShadow: `0 24px 80px rgba(167,139,250,0.18), 0 0 0 1px #2D2A5E`, overflow: "hidden", position: "relative" }}>
        {/* Notch */}
        <div style={{ height: 28, background: "#0D0A1E", display: "flex", alignItems: "center", justifyContent: "center", borderBottom: `1px solid ${C.border}` }}>
          <div style={{ width: 60, height: 10, background: C.surface, borderRadius: 5 }} />
        </div>
        {/* Mini app header */}
        <div style={{ padding: "10px 12px 6px", background: C.surface, borderBottom: `1px solid ${C.border}` }}>
          <div style={{ fontSize: 11, fontWeight: 800, color: C.accent, fontFamily: "'Georgia', serif" }}>❤️‍🔥 dsb</div>
        </div>
        {/* Mini board */}
        <div style={{ padding: "10px 10px 0" }}>
          <div style={{ fontSize: 9, fontWeight: 700, color: C.textMuted, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 }}>Active · 3</div>
          {[
            { name: "Alex", mood: "🔥", dates: "3 dates", note: "Planning weekend trip", stageColor: C.green },
            { name: "Rohan", mood: "💚", dates: "0 dates", note: "Coffee Saturday morning", stageColor: C.purple },
            { name: "Dev", mood: "🤔", dates: "0 dates", note: "Situationship risk", stageColor: C.blue },
          ].map((c, i) => (
            <div key={i} style={{ background: C.card, borderRadius: 8, padding: "7px 9px", marginBottom: 6, border: `1px solid ${C.border}` }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 2 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                  <span style={{ fontSize: 12 }}>{c.mood}</span>
                  <span style={{ fontSize: 10, fontWeight: 700, color: C.text }}>{c.name}</span>
                </div>
                <span style={{ fontSize: 9, color: C.textMuted }}>{c.dates}</span>
              </div>
              <div style={{ fontSize: 9, color: C.textMuted, overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>{c.note}</div>
            </div>
          ))}
          <div style={{ fontSize: 9, fontWeight: 700, color: C.textMuted, textTransform: "uppercase", letterSpacing: 0.5, margin: "8px 0 6px" }}>Going Well ❤️ · 1</div>
          <div style={{ background: C.card, borderRadius: 8, padding: "7px 9px", marginBottom: 10, border: `1px solid ${C.accent}44` }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 2 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                <span style={{ fontSize: 12 }}>🔥</span>
                <span style={{ fontSize: 10, fontWeight: 700, color: C.text }}>Nikhil</span>
              </div>
              <span style={{ fontSize: 9, color: C.textMuted }}>24 dates</span>
            </div>
            <div style={{ fontSize: 9, color: C.textMuted }}>Met his parents last week</div>
          </div>
        </div>
        {/* Mini bottom nav */}
        <div style={{ display: "flex", justifyContent: "space-around", padding: "8px 0 12px", background: C.surface, borderTop: `1px solid ${C.border}` }}>
          {["🏠", "📋", "📊", "🎁", "🧠"].map((icon, i) => (
            <span key={i} style={{ fontSize: i === 1 ? 14 : 11, opacity: i === 1 ? 1 : 0.45 }}>{icon}</span>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <div style={{ minHeight: "100vh", paddingBottom: mob ? 80 : 0 }}>
      {/* HERO */}
      <div style={{ padding: mob ? "56px 20px 0" : "88px 24px 0", textAlign: "center", maxWidth: 720, margin: "0 auto" }}>
        <div style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "5px 14px", borderRadius: 20, background: C.accentSoft, border: `1px solid ${C.accent}44`, color: C.accent, fontSize: mob ? 10 : 11, fontWeight: 600, letterSpacing: 1, textTransform: "uppercase", marginBottom: 28 }}>
          <span style={{ width: 6, height: 6, borderRadius: 3, background: C.accent, display: "inline-block" }} />
          Available Now
        </div>
        <h1 style={{ fontSize: mob ? "clamp(34px, 9vw, 52px)" : "clamp(44px, 6vw, 68px)", fontWeight: 800, color: C.text, lineHeight: 1.05, margin: "0 0 20px", letterSpacing: mob ? -1 : -2, fontFamily: "'Georgia', serif" }}>
          Your dating life,<br />
          <span style={{ background: `linear-gradient(135deg, ${C.accent}, ${C.purple})`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>finally making sense.</span>
        </h1>
        <p style={{ fontSize: mob ? 15 : 18, color: C.textMuted, maxWidth: 520, margin: "0 auto 36px", lineHeight: 1.65 }}>
          Track every connection, spot your patterns, get coached by AI that learns your specific dating history.
        </p>
        <button onClick={() => setActive("board")} style={{ padding: mob ? "14px 28px" : "16px 36px", borderRadius: 12, border: "none", background: `linear-gradient(135deg, ${C.accent}, #FF4757)`, color: "white", fontSize: mob ? 15 : 16, fontWeight: 700, cursor: "pointer", boxShadow: `0 8px 32px ${C.accentGlow}`, fontFamily: "inherit", letterSpacing: 0.3 }}>
          Try the Demo →
        </button>

        <PhoneMockup />
      </div>

      {/* SOCIAL PROOF */}
      <div style={{ borderTop: `1px solid ${C.border}`, borderBottom: `1px solid ${C.border}`, background: C.surface, marginTop: 48, padding: mob ? "18px 20px" : "20px 24px" }}>
        <div style={{ maxWidth: 720, margin: "0 auto", display: "flex", alignItems: "center", justifyContent: "center", gap: 12, flexWrap: "wrap" }}>
          <div style={{ display: "flex", gap: -4 }}>
            {["👩‍💻", "👩", "👩‍🦱", "🧑", "👨"].map((av, i) => (
              <div key={i} style={{ width: 28, height: 28, borderRadius: 14, background: C.card, border: `2px solid ${C.surface}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, marginLeft: i === 0 ? 0 : -8 }}>{av}</div>
            ))}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <div style={{ display: "flex", gap: 2 }}>
              {[1,2,3,4,5].map(i => <span key={i} style={{ color: "#FBBF24", fontSize: 12 }}>★</span>)}
            </div>
            <span style={{ fontSize: mob ? 12 : 13, color: C.textMuted, fontWeight: 500 }}>
              <span style={{ color: C.text, fontWeight: 700 }}>65K+ followers</span> trust Surbhi's dating advice
            </span>
          </div>
        </div>
      </div>

      {/* FEATURE HIGHLIGHTS */}
      <div style={{ maxWidth: 960, margin: "0 auto", padding: mob ? "48px 20px" : "64px 24px" }}>
        <div style={{ display: "grid", gridTemplateColumns: mob ? "1fr" : "repeat(3, 1fr)", gap: 16 }}>
          {[
            { icon: "📋", title: "Track Every Date", desc: "One board. Every person, every stage, one-line status. See your entire dating life at a glance.", color: C.blue, colorSoft: C.blueSoft },
            { icon: "📊", title: "See Your Patterns", desc: "Your data reveals what your heart hides. Ghost patterns, love bombers, calm scores — before it's too late.", color: C.purple, colorSoft: C.purpleSoft },
            { icon: "🧠", title: "Get AI Coaching", desc: "Tell me what happened. I'll tell you what it means, what your pattern is, and exactly what to do next.", color: C.accent, colorSoft: C.accentSoft },
          ].map((f, i) => (
            <div key={i} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 20, padding: mob ? "24px 22px" : "32px 28px" }}>
              <div style={{ width: 44, height: 44, borderRadius: 14, background: f.colorSoft, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, marginBottom: 16, border: `1px solid ${f.color}33` }}>{f.icon}</div>
              <div style={{ fontSize: mob ? 16 : 17, fontWeight: 700, color: C.text, marginBottom: 8 }}>{f.title}</div>
              <div style={{ fontSize: mob ? 13 : 14, color: C.textMuted, lineHeight: 1.65 }}>{f.desc}</div>
            </div>
          ))}
        </div>
      </div>

      {/* BOTTOM CTA */}
      <div style={{ textAlign: "center", padding: mob ? "0 20px 64px" : "0 24px 88px" }}>
        <p style={{ fontSize: mob ? 13 : 15, color: C.textMuted, marginBottom: 20 }}>See your own patterns. No account needed.</p>
        <button onClick={() => setActive("board")} style={{ padding: mob ? "14px 28px" : "16px 36px", borderRadius: 12, border: `1px solid ${C.border}`, background: C.surface, color: C.text, fontSize: mob ? 14 : 15, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
          Try the Demo →
        </button>
      </div>
    </div>
  );
}

// ============ BOARD ============

const BOARD_SECTIONS = [
  { id: "active", label: "Active", stages: ["talking", "first_date", "dating"], borderColor: null },
  { id: "going_well", label: "Going Well ❤️", stages: ["exclusive"], borderColor: "#FF6B6B" },
  { id: "ended", label: "Ended 👻", stages: ["ended"], borderColor: null, dim: true },
];

function BoardPage() {
  const [selected, setSelected] = useState(null);
  const mob = useIsMobile();

  return (
    <div style={{ padding: mob ? "20px 12px" : "32px 24px", maxWidth: 1100, margin: "0 auto" }}>
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ fontSize: mob ? 22 : 28, fontWeight: 800, color: C.text, margin: 0, fontFamily: "'Georgia', serif" }}>Your Dating Board</h2>
        <p style={{ color: C.textMuted, margin: "8px 0 0", fontSize: mob ? 13 : 14 }}>Every person. Every stage. One-line status. {mob ? "Tap" : "Click"} for the full picture.</p>
      </div>

      {/* Stats */}
      <div style={{ display: "grid", gridTemplateColumns: mob ? "1fr 1fr" : "repeat(4, 1fr)", gap: 12, marginBottom: 24 }}>
        <StatCard label="Active" value={SAMPLE_DATES.filter(d => d.stage !== "ended").length} sublabel="in rotation" color={C.green} />
        <StatCard label="Total Dates" value="33" sublabel="all time" color={C.purple} />
        <StatCard label="Avg Calm" value="7.2" sublabel="↑ from 4.8" color={C.blue} />
        <StatCard label="Ghosted" value="2" sublabel="↓ from 5" color={C.accent} />
      </div>

      {/* 3-section kanban */}
      <div style={{ display: "flex", gap: 12, overflowX: "auto", paddingBottom: 8, WebkitOverflowScrolling: "touch", alignItems: "flex-start" }}>
        {BOARD_SECTIONS.map(section => {
          const sectionCards = SAMPLE_DATES.filter(d => section.stages.includes(d.stage));
          return (
            <div key={section.id} style={{ background: C.surface, borderRadius: 16, border: `1px solid ${section.borderColor ? section.borderColor + "44" : C.border}`, overflow: "hidden", minWidth: mob ? 240 : 0, flex: mob ? "0 0 240px" : 1, opacity: section.dim ? 0.8 : 1 }}>
              {/* Section header */}
              <div style={{ padding: "12px 14px", borderBottom: `1px solid ${section.borderColor ? section.borderColor + "33" : C.border}`, display: "flex", alignItems: "center", gap: 8, background: section.borderColor ? section.borderColor + "08" : "transparent" }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: section.borderColor || C.text }}>{section.label}</span>
                <span style={{ fontSize: 11, color: C.textMuted, background: C.card, padding: "1px 8px", borderRadius: 10, marginLeft: "auto", fontWeight: 600 }}>{sectionCards.length}</span>
              </div>
              {/* Cards */}
              <div style={{ padding: 8, display: "flex", flexDirection: "column", gap: 7, minHeight: 180 }}>
                {sectionCards.map(p => {
                  const stageInfo = STAGES[p.stage];
                  const isSelected = selected?.id === p.id;
                  return (
                    <div key={p.id} onClick={() => setSelected(isSelected ? null : p)}
                      style={{ background: isSelected ? C.surfaceHover : C.card, border: `1px solid ${isSelected ? (section.borderColor || stageInfo.color) + "66" : C.border}`, borderRadius: 12, padding: "10px 12px", cursor: "pointer", transition: "all 0.15s" }}>
                      {/* Name row */}
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                          <span style={{ fontSize: 16 }}>{p.mood}</span>
                          <span style={{ fontSize: 13, fontWeight: 700, color: C.text }}>{p.name.split(" ")[0]}</span>
                        </div>
                        <span style={{ fontSize: 10, color: C.textMuted, background: C.surface, padding: "2px 7px", borderRadius: 8, fontWeight: 600 }}>{p.dateCount} dates</span>
                      </div>
                      {/* Sub-stage + platform */}
                      <div style={{ display: "flex", alignItems: "center", gap: 5, marginBottom: 6 }}>
                        <span style={{ fontSize: 9, color: stageInfo.color, background: stageInfo.bg, padding: "2px 7px", borderRadius: 8, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.3 }}>{stageInfo.label}</span>
                        <span style={{ fontSize: 10, color: C.textDim }}>{p.platform}</span>
                      </div>
                      {/* 1-line takeaway */}
                      <div style={{ fontSize: 11, color: section.dim ? C.textDim : C.textMuted, lineHeight: 1.4, overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis", fontStyle: section.dim ? "italic" : "normal" }}>
                        {p.lastUpdate}
                      </div>
                    </div>
                  );
                })}
                {sectionCards.length === 0 && (
                  <div style={{ padding: 20, textAlign: "center", color: C.textDim, fontSize: 11, fontStyle: "italic" }}>Empty</div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Expanded detail panel */}
      {selected && (
        <div style={{ marginTop: 20, background: C.card, border: `1px solid ${C.border}`, borderRadius: 20, padding: mob ? 18 : 28 }}>
          <div style={{ display: "flex", flexDirection: mob ? "column" : "row", justifyContent: "space-between", gap: mob ? 16 : 24 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
                <div style={{ width: 48, height: 48, borderRadius: 14, background: STAGES[selected.stage].bg, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24 }}>{selected.avatar}</div>
                <div>
                  <h3 style={{ margin: 0, fontSize: mob ? 18 : 20, fontWeight: 700, color: C.text }}>{selected.name}</h3>
                  <div style={{ display: "flex", gap: 8, marginTop: 4, flexWrap: "wrap" }}>
                    <Badge color={STAGES[selected.stage].color} bg={STAGES[selected.stage].bg}>{STAGES[selected.stage].label}</Badge>
                    <span style={{ fontSize: 12, color: C.textMuted }}>{selected.platform} · Met {selected.met}</span>
                  </div>
                </div>
              </div>
              <div style={{ background: C.surface, borderRadius: 10, padding: 14, border: `1px solid ${C.border}`, marginBottom: 10 }}>
                <div style={{ fontSize: 11, color: C.textMuted, textTransform: "uppercase", letterSpacing: 1, marginBottom: 6 }}>Latest</div>
                <div style={{ fontSize: 14, color: C.text, lineHeight: 1.5 }}>{selected.lastUpdate}</div>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                <div style={{ background: C.surface, borderRadius: 10, padding: 12, border: `1px solid ${C.border}`, textAlign: "center" }}>
                  <div style={{ fontSize: 22, fontWeight: 700, color: C.green }}>{selected.dateCount}</div>
                  <div style={{ fontSize: 11, color: C.textMuted }}>dates</div>
                </div>
                <div style={{ background: C.surface, borderRadius: 10, padding: 12, border: `1px solid ${C.border}`, textAlign: "center" }}>
                  <div style={{ fontSize: 22, fontWeight: 700, color: selected.calm >= 7 ? C.blue : C.amber }}>{selected.calm}/10</div>
                  <div style={{ fontSize: 11, color: C.textMuted }}>calm score</div>
                </div>
              </div>
            </div>
            <div style={{ width: mob ? "100%" : 260, height: 200 }}>
              <div style={{ fontSize: 11, color: C.textMuted, textTransform: "uppercase", letterSpacing: 1, marginBottom: 6 }}>Compatibility</div>
              <ResponsiveContainer width="100%" height="100%">
                <RadarChart data={[{ t: "Vibe", v: selected.vibe }, { t: "Effort", v: selected.effort }, { t: "Calm", v: selected.calm }, { t: "Values", v: selected.values }, { t: "Chemistry", v: selected.chemistry }]}>
                  <PolarGrid stroke={C.border} /><PolarAngleAxis dataKey="t" tick={{ fill: C.textMuted, fontSize: 11 }} /><PolarRadiusAxis domain={[0, 10]} tick={false} axisLine={false} />
                  <Radar dataKey="v" stroke={C.accent} fill={C.accent} fillOpacity={0.2} strokeWidth={2} />
                </RadarChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div style={{ marginTop: 14, padding: 14, borderRadius: 10, background: `linear-gradient(135deg, ${C.accentSoft}, ${C.purpleSoft})`, border: `1px solid ${C.accent}33` }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: C.accent, marginBottom: 4 }}>🧠 AI Insight</div>
            <div style={{ fontSize: 13, color: C.text, lineHeight: 1.5 }}>
              {selected.calm >= 7 && selected.effort >= 7
                ? `${selected.name.split(" ")[0]} hits your best pattern: high calm + high effort. This is what works for you long-term. The chemistry builds slower but lasts.`
                : selected.chemistry >= 8 && selected.calm <= 4
                ? `Warning: ${selected.name.split(" ")[0]} triggers your "exciting but unstable" pattern. High chemistry (${selected.chemistry}) with low calm (${selected.calm}). This is how situationships start for you.`
                : selected.effort <= 4
                ? `${selected.name.split(" ")[0]}'s effort (${selected.effort}/10) is below your stated minimum. You said effort is non-negotiable. This is your wingman reminding you.`
                : `${selected.name.split(" ")[0]} is solid but still early. Keep logging. The pattern will clarify by date 3.`}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============ INSIGHTS ============

function InsightsPage() {
  const mob = useIsMobile();
  return (
    <div style={{ padding: mob ? "20px 12px" : "32px 24px", maxWidth: 1000, margin: "0 auto" }}>
      <h2 style={{ fontSize: mob ? 22 : 28, fontWeight: 800, color: C.text, margin: "0 0 8px", fontFamily: "'Georgia', serif" }}>Your Dating Patterns</h2>
      <p style={{ color: C.textMuted, margin: "0 0 28px", fontSize: 14 }}>Imported from Hinge + your logged dates. Updated live.</p>

      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 20, padding: mob ? 16 : 28, marginBottom: 16 }}>
        <h3 style={{ fontSize: 16, fontWeight: 700, color: C.text, margin: "0 0 4px" }}>Your Picker is Getting Better</h3>
        <p style={{ fontSize: 13, color: C.textMuted, margin: "0 0 20px" }}>High-effort dates up, ghosting down, calm score climbing.</p>
        <ResponsiveContainer width="100%" height={mob ? 180 : 220}>
          <AreaChart data={[{ w: "W1", calm: 4, effort: 3, ghost: 3 }, { w: "W2", calm: 5, effort: 4, ghost: 2 }, { w: "W3", calm: 5, effort: 5, ghost: 2 }, { w: "W4", calm: 7, effort: 7, ghost: 1 }, { w: "W5", calm: 7, effort: 8, ghost: 0 }, { w: "W6", calm: 8, effort: 8, ghost: 0 }]}>
            <CartesianGrid strokeDasharray="3 3" stroke={C.border} /><XAxis dataKey="w" tick={{ fill: C.textMuted, fontSize: 12 }} /><YAxis tick={{ fill: C.textMuted, fontSize: 12 }} domain={[0, 10]} />
            <Tooltip contentStyle={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, color: C.text }} />
            <Area type="monotone" dataKey="calm" stroke={C.blue} fill={C.blueSoft} name="Calm Score" /><Area type="monotone" dataKey="effort" stroke={C.green} fill={C.greenSoft} name="Their Effort" /><Area type="monotone" dataKey="ghost" stroke={C.accent} fill={C.accentSoft} name="Ghosted/Ended" />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: `repeat(auto-fit, minmax(min(300px, 100%), 1fr))`, gap: 16 }}>
        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 20, padding: mob ? 18 : 24 }}>
          <h3 style={{ fontSize: 15, fontWeight: 700, color: C.text, margin: "0 0 16px" }}>Hinge Import: Your Type</h3>
          {[
            { label: "Most liked age range", value: "26-30", color: C.blue },
            { label: "Most liked height", value: "5'10\"+", color: C.purple },
            { label: "Match-to-chat rate", value: "34%", color: C.green },
            { label: "Chat-to-date rate", value: "12%", color: C.amber },
            { label: "Avg convo length before date", value: "4.2 days", color: C.accent },
            { label: "Ghost rate (them)", value: "41%", color: C.rose },
          ].map((s, i) => (
            <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: i < 5 ? `1px solid ${C.border}` : "none" }}>
              <span style={{ fontSize: 13, color: C.textMuted }}>{s.label}</span>
              <span style={{ fontSize: 13, fontWeight: 600, color: s.color }}>{s.value}</span>
            </div>
          ))}
          <div style={{ marginTop: 14, padding: 10, borderRadius: 8, background: C.purpleSoft, fontSize: 12, color: C.text, lineHeight: 1.5 }}>🧠 Your chat-to-date conversion is low. 68% of your convos die after 3 messages. The fix: propose a plan by message 4-5.</div>
        </div>

        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 20, padding: mob ? 18 : 24 }}>
          <h3 style={{ fontSize: 15, fontWeight: 700, color: C.text, margin: "0 0 16px" }}>Pattern Alerts</h3>
          {[
            { icon: "🚩", label: "Love bomber detected", desc: "James K. hit 5 of 7 love bombing signals by date 2. You didn't see it until date 4.", color: C.accent },
            { icon: "👻", label: "Ghost pattern", desc: "3 of your last 5 'ended' connections ghosted after date 2. Common factor: you didn't establish next plans on the date.", color: C.amber },
            { icon: "😐", label: "Situationship risk", desc: "You've been 'talking' to Dev M. for 8 days with no date planned. This is how your last 3 situationships started.", color: C.rose },
            { icon: "✅", label: "Growth moment", desc: "First time you moved someone from 'Paused' to 'Ended' based on low effort score. That's boundary-setting in action.", color: C.green },
          ].map((a, i) => (
            <div key={i} style={{ display: "flex", gap: 10, padding: "10px 0", borderBottom: i < 3 ? `1px solid ${C.border}` : "none" }}>
              <span style={{ fontSize: 18 }}>{a.icon}</span>
              <div><div style={{ fontSize: 13, fontWeight: 600, color: a.color }}>{a.label}</div><div style={{ fontSize: 12, color: C.textMuted, lineHeight: 1.5 }}>{a.desc}</div></div>
            </div>
          ))}
        </div>

        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 20, padding: mob ? 18 : 24 }}>
          <h3 style={{ fontSize: 15, fontWeight: 700, color: C.text, margin: "0 0 16px" }}>Values: Said vs. Reality</h3>
          {[
            { l: "Effort & Initiative", s: true, a: true, i: "✅" },
            { l: "Emotional Availability", s: true, a: true, i: "✅" },
            { l: "Physical Attraction", s: false, a: true, i: "🔍" },
            { l: "Ambitious", s: true, a: false, i: "🤔" },
            { l: "Doesn't Split Bill", s: true, a: false, i: "🤔" },
          ].map((v, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 0", borderBottom: i < 4 ? `1px solid ${C.border}` : "none" }}>
              <span style={{ fontSize: 14 }}>{v.i}</span><span style={{ fontSize: 12, color: C.text, flex: 1 }}>{v.l}</span>
              <Badge color={v.a ? C.blue : C.amber} bg={v.a ? C.blueSoft : C.amberSoft}>{v.a ? "Real" : "Aspirational"}</Badge>
            </div>
          ))}
          <div style={{ marginTop: 12, padding: 10, borderRadius: 8, background: C.accentSoft, fontSize: 12, color: C.text, lineHeight: 1.5 }}>🧠 You didn't list physical attraction as a value, but it predicts who you keep seeing better than anything else. Own it.</div>
        </div>

        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 20, padding: mob ? 18 : 24 }}>
          <h3 style={{ fontSize: 15, fontWeight: 700, color: C.text, margin: "0 0 16px" }}>Dating Growth Score</h3>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            {[
              { l: "Self-Awareness", s: 8.2, d: "+1.4", c: C.green },
              { l: "Boundaries", s: 6.5, d: "+2.1", c: C.blue },
              { l: "Communication", s: 7.8, d: "+0.8", c: C.purple },
              { l: "Pattern Breaking", s: 5.9, d: "+3.2", c: C.accent },
            ].map((s, i) => (
              <div key={i} style={{ background: C.surface, borderRadius: 10, padding: 14, border: `1px solid ${C.border}`, textAlign: "center" }}>
                <div style={{ fontSize: 24, fontWeight: 800, color: s.c }}>{s.s}</div>
                <div style={{ fontSize: 11, color: C.text, marginTop: 2 }}>{s.l}</div>
                <div style={{ fontSize: 11, color: C.green, marginTop: 2 }}>{s.d}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ============ DATING WRAPPED ============

function WrappedPage() {
  const mob = useIsMobile();
  return (
    <div style={{ padding: mob ? "20px 12px" : "32px 24px", maxWidth: 700, margin: "0 auto" }}>
      <h2 style={{ fontSize: mob ? 22 : 28, fontWeight: 800, color: C.text, margin: "0 0 8px", fontFamily: "'Georgia', serif" }}>Your Dating Wrapped</h2>
      <p style={{ color: C.textMuted, margin: "0 0 32px", fontSize: 14 }}>Like Spotify Wrapped, but for your love life. Updates live.</p>

      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        {[
          { bg: `linear-gradient(135deg, #1a1a2e, ${C.accent}22)`, border: C.accent, content: (
            <div style={{ textAlign: "center", padding: mob ? "28px 16px" : "40px 20px" }}>
              <div style={{ fontSize: 11, color: C.accent, textTransform: "uppercase", letterSpacing: 2, marginBottom: 16 }}>Since you started</div>
              <div style={{ fontSize: mob ? 44 : 56, fontWeight: 800, color: C.text }}>33</div>
              <div style={{ fontSize: mob ? 14 : 16, color: C.textMuted, marginBottom: 24 }}>dates logged</div>
              <div style={{ display: "flex", justifyContent: "center", gap: mob ? 20 : 32 }}>
                <div><div style={{ fontSize: mob ? 20 : 24, fontWeight: 700, color: C.green }}>6</div><div style={{ fontSize: 11, color: C.textMuted }}>people dated</div></div>
                <div><div style={{ fontSize: mob ? 20 : 24, fontWeight: 700, color: C.purple }}>1</div><div style={{ fontSize: 11, color: C.textMuted }}>relationship</div></div>
                <div><div style={{ fontSize: mob ? 20 : 24, fontWeight: 700, color: C.accent }}>2</div><div style={{ fontSize: 11, color: C.textMuted }}>ghosted</div></div>
              </div>
            </div>
          )},
          { bg: `linear-gradient(135deg, #1a1a2e, ${C.purple}22)`, border: C.purple, content: (
            <div style={{ textAlign: "center", padding: mob ? "28px 16px" : "40px 20px" }}>
              <div style={{ fontSize: 11, color: C.purple, textTransform: "uppercase", letterSpacing: 2, marginBottom: 16 }}>Your biggest growth</div>
              <div style={{ fontSize: mob ? 28 : 40, fontWeight: 800, color: C.text, lineHeight: 1.2 }}>Pattern Breaking</div>
              <div style={{ fontSize: mob ? 24 : 32, fontWeight: 800, color: C.green, margin: "8px 0" }}>+3.2 points</div>
              <div style={{ fontSize: 14, color: C.textMuted, maxWidth: 400, margin: "0 auto" }}>You stopped chasing love bombers. Your calm score went from 4.1 to 7.8. That's not luck. That's growth.</div>
            </div>
          )},
          { bg: `linear-gradient(135deg, #1a1a2e, ${C.blue}22)`, border: C.blue, content: (
            <div style={{ textAlign: "center", padding: mob ? "28px 16px" : "40px 20px" }}>
              <div style={{ fontSize: 11, color: C.blue, textTransform: "uppercase", letterSpacing: 2, marginBottom: 16 }}>Your dating personality</div>
              <div style={{ fontSize: mob ? 28 : 40, fontWeight: 800, color: C.text, lineHeight: 1.2 }}>The Overthinking Romantic</div>
              <div style={{ fontSize: mob ? 13 : 14, color: C.textMuted, maxWidth: 420, margin: "12px auto 0" }}>You feel deeply, analyze everything, and care almost too much. Your superpower: emotional intelligence. Your kryptonite: reading into silences. Your best matches: calm, direct communicators who match your effort.</div>
            </div>
          )},
          { bg: `linear-gradient(135deg, #1a1a2e, ${C.green}22)`, border: C.green, content: (
            <div style={{ textAlign: "center", padding: mob ? "28px 16px" : "40px 20px" }}>
              <div style={{ fontSize: 11, color: C.green, textTransform: "uppercase", letterSpacing: 2, marginBottom: 16 }}>Highlight reel</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 12, maxWidth: 360, margin: "0 auto", textAlign: "left" }}>
                {[
                  "🏆 Set a boundary for the first time (moved James to Ended)",
                  "📈 Calm score broke 7.0 for the first time in Week 4",
                  "💚 Went on 3 consecutive high-effort dates",
                  "🎯 Values alignment accuracy improved 40%",
                  "❤️ Found someone who checks all your real (not aspirational) boxes",
                ].map((h, i) => (
                  <div key={i} style={{ fontSize: 13, color: C.text, lineHeight: 1.4 }}>{h}</div>
                ))}
              </div>
            </div>
          )},
        ].map((card, i) => (
          <div key={i} style={{ background: card.bg, border: `1px solid ${card.border}33`, borderRadius: 20, overflow: "hidden" }}>
            {card.content}
          </div>
        ))}
        <div style={{ textAlign: "center", padding: 20 }}>
          <button style={{ padding: "12px 28px", borderRadius: 12, border: "none", background: C.accent, color: "white", fontWeight: 700, cursor: "pointer", fontSize: 14, fontFamily: "inherit" }}>Share My Wrapped 📤</button>
          <div style={{ fontSize: 12, color: C.textMuted, marginTop: 8 }}>This is the viral loop. People share their dating personality type.</div>
        </div>
      </div>
    </div>
  );
}

// ============ AI WINGMAN ============

function CoachPage() {
  const mob = useIsMobile();
  const messages = [
    { role: "user", text: "[Screenshot of Hinge profile: Priya, 26. Golden retriever in 3 photos, hiking, farmers market, reading in a park]" },
    { role: "assistant", text: "Alright, reading Priya. Clear signals:\n\n🐕 That dog is her entire personality. Lead with it: 'I need to know the dog's name before anything else.' Trust me, works every time with dog people.\n\n☕ She's NOT a dinner girl. Don't take her somewhere fancy. Suggest a walk: 'There's a great trail near Prospect Park. Grab coffees and walk?' She'll love that over any restaurant.\n\n🧺 Second date move: pack a picnic. Blanket, cheese, fruit, wine. Her profile screams low-key effort over flashy spending.\n\n📊 Pattern match: Priya's energy is closest to Rohan on your board. Calm, thoughtful, effort through presence. That's your best zone based on 6 weeks of data." },
    { role: "user", text: "Okay what about this one. I met this girl Maya at my friend's birthday last night. She works in publishing, was wearing this vintage jacket, kept making everyone laugh. We talked about books for like an hour and she just moved to Brooklyn from Chicago. I got her number but idk what to text" },
    { role: "assistant", text: "Love this. IRL meet. Your board shows 7.5 avg compatibility for IRL vs 5.8 for apps.\n\nThe text (send today, around 4-5pm):\n\n\"Hey, it's [you] from last night. I've been thinking about that book you mentioned and I think you might be completely wrong about the ending. Argue about it over drinks this week?\"\n\nWhy this works:\n→ Specific (shows you actually listened)\n→ Playful (not 'hey it was nice meeting you')\n→ Proposes a real plan (drinks this week, not 'sometime')\n\nDon't overthink the timing. Today is perfect. Not desperate, not playing games.\n\n📋 Adding Maya to your board: Talking stage. IRL connection, shared intellectual interests, high early chemistry. I'll check in tomorrow to see if she responded." },
    { role: "user", text: "she responded! she said 'haha I stand by my take but I'm down to argue over drinks. thursday work?'" },
    { role: "assistant", text: "That's a great response. She proposed a specific day. That's effort. That's a green flag.\n\nYour reply (send within the hour, don't overthink):\n\n\"Thursday is perfect. I know a great spot in the Village, I'll send you the details tomorrow. Fair warning, I'm bringing receipts for my argument.\"\n\nWhy: you took the lead on the plan (she'll appreciate that), you kept the playful thread going, and saying 'tomorrow' builds anticipation without over-texting tonight.\n\nFor Thursday:\n📍 She just moved to Brooklyn and she's into books/vintage. Try Housing Works Bookstore Cafe for drinks, or if you want food, Misi in Williamsburg.\n👕 She was wearing a vintage jacket. Go smart-casual, no suit energy. Something that says 'I have taste but I'm not trying too hard.'\n🗣️ Topics: publishing industry (ask what she's working on), Chicago vs NYC take, what she's reading right now. Skip: 'so what do you do for fun' (boring).\n\nYour calm score prediction for Maya: 7+. She's funny, direct, and proposed a day. No games. I like this one for you." },
  ];

  const [msgs, setMsgs] = useState(messages);
  const [input, setInput] = useState("");
  const [typing, setTyping] = useState(false);
  const ref = useRef(null);
  useEffect(() => { ref.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);

  const responses = [
    "Send me a screenshot and I'll break it down. I can tell you dinner girl vs. coffee girl, what to talk about, and how she compares to your patterns.",
    "Okay based on what you've told me, here's the play. She mentioned she loves cooking. Don't take her to a restaurant. Invite her to cook together. It's intimate and it signals you listened. I'm logging this.",
    "I know you. You're about to triple-text. Put the phone down. She'll respond. And if she doesn't by tomorrow, I'll help you figure out if it's ghosting or just busy.",
    "Cross-referencing with your values data: she checks 4 of 5. The unknown is conflict style. That shows up by date 3. I'll flag it when we debrief.",
  ];

  const send = () => { if (!input.trim()) return; setMsgs(p => [...p, { role: "user", text: input }]); setInput(""); setTyping(true); setTimeout(() => { setMsgs(p => [...p, { role: "assistant", text: responses[Math.floor(Math.random() * responses.length)] }]); setTyping(false); }, 1500); };

  return (
    <div style={{ padding: mob ? "20px 12px" : "32px 24px", maxWidth: 700, margin: "0 auto", display: "flex", flexDirection: "column", height: "calc(100dvh - 120px)" }}>
      {/* Header with AI avatar */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: mob ? 10 : 16 }}>
        <div style={{ width: 40, height: 40, borderRadius: "50%", background: C.card, border: `2px solid ${C.accent}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, flexShrink: 0 }}>🧠</div>
        <div>
          <h2 style={{ fontSize: mob ? 20 : 24, fontWeight: 800, color: C.text, margin: 0, fontFamily: "'Georgia', serif" }}>Your AI Wingman</h2>
          {!mob && <p style={{ color: C.textMuted, margin: 0, fontSize: 12 }}>Screenshot profiles. Paste convos. Voice-note debriefs.</p>}
        </div>
        <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 5 }}>
          <div style={{ width: 7, height: 7, borderRadius: "50%", background: C.green }} />
          <span style={{ fontSize: 11, color: C.textMuted }}>Online</span>
        </div>
      </div>

      {/* Quick actions */}
      <div style={{ display: "flex", gap: 6, marginBottom: 12, flexWrap: "wrap" }}>
        {(mob
          ? ["📸 Analyze a profile", "💬 What should I text?", "🍷 Plan a date", "👻 Why ghosted?"]
          : ["📸 Analyze a profile", "💬 What should I text?", "🍷 Plan a date", "🎙️ Post-date debrief", "👻 Why did they ghost me?", "😰 I'm overthinking"]
        ).map((a, i) => (
          <button key={i} onClick={() => setInput(a.slice(2))} style={{ padding: mob ? "6px 10px" : "7px 12px", borderRadius: 20, border: `1px solid ${C.border}`, background: C.surface, color: C.textMuted, fontSize: mob ? 10 : 11, cursor: "pointer", fontFamily: "inherit", transition: "color 0.15s, border-color 0.15s" }}>{a}</button>
        ))}
      </div>

      {/* Messages */}
      <div style={{ flex: 1, overflowY: "auto", display: "flex", flexDirection: "column", gap: 12, paddingRight: mob ? 0 : 8, WebkitOverflowScrolling: "touch" }}>
        {msgs.map((m, i) => (
          <div key={i} style={{ alignSelf: m.role === "user" ? "flex-end" : "flex-start", maxWidth: mob ? "92%" : "82%" }}>
            {m.role === "assistant" && <div style={{ fontSize: 10, color: C.textMuted, marginBottom: 4, fontWeight: 600, letterSpacing: 0.3 }}>DSB AI</div>}
            <div style={{
              padding: "12px 16px", borderRadius: 18, lineHeight: 1.65, whiteSpace: "pre-line", fontSize: 13,
              background: m.role === "user" ? C.accent : C.card,
              border: m.role === "user" ? "none" : `1px solid ${C.border}`,
              color: m.role === "user" ? "white" : C.text,
              borderTopRightRadius: m.role === "user" ? 4 : 18,
              borderTopLeftRadius: m.role === "user" ? 18 : 4,
              boxShadow: m.role === "user" ? `0 2px 12px ${C.accentGlow}` : "none",
            }}>{m.text}</div>
          </div>
        ))}
        {typing && (
          <div style={{ alignSelf: "flex-start", padding: "12px 18px", background: C.card, border: `1px solid ${C.border}`, borderRadius: 18, borderTopLeftRadius: 4, display: "flex", gap: 4, alignItems: "center" }}>
            <span className="typing-dot" style={{ width: 7, height: 7, borderRadius: "50%", background: C.textMuted, display: "inline-block" }} />
            <span className="typing-dot" style={{ width: 7, height: 7, borderRadius: "50%", background: C.textMuted, display: "inline-block", animationDelay: "0.15s" }} />
            <span className="typing-dot" style={{ width: 7, height: 7, borderRadius: "50%", background: C.textMuted, display: "inline-block", animationDelay: "0.3s" }} />
          </div>
        )}
        <div ref={ref} />
      </div>

      {/* Input bar with coral top accent */}
      <div style={{ display: "flex", gap: 8, marginTop: 12, padding: "0 8px 8px", background: C.surface, border: `1px solid ${C.border}`, borderTop: `2px solid ${C.accent}`, borderRadius: "0 0 14px 14px" }}>
        <div style={{ display: "flex", gap: 8, flex: 1, paddingTop: 8 }}>
          {!mob && <button style={{ width: 36, height: 36, borderRadius: 8, border: `1px solid ${C.border}`, background: C.card, color: C.accent, fontSize: 16, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>🎙️</button>}
          {!mob && <button style={{ width: 36, height: 36, borderRadius: 8, border: `1px solid ${C.border}`, background: C.card, color: C.blue, fontSize: 16, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>📸</button>}
          <input value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => e.key === "Enter" && send()} placeholder={mob ? "Tell me what happened..." : "Screenshot, voice note, or just tell me..."} style={{ flex: 1, background: "none", border: "none", color: C.text, fontSize: 13, outline: "none", fontFamily: "inherit", minWidth: 0, paddingTop: 8 }} />
          <button onClick={send} style={{ width: 36, height: 36, borderRadius: 8, border: "none", background: C.accent, color: "white", fontSize: 14, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 2 }}>→</button>
        </div>
      </div>
    </div>
  );
}


// ============ MAIN ============

export default function App() {
  const [active, setActive] = useState("landing");
  const mob = useIsMobile();
  return (
    <div style={{ minHeight: "100vh", background: C.bg, color: C.text, fontFamily: "'Instrument Sans', 'DM Sans', -apple-system, sans-serif", position: "relative", overflow: "hidden" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Instrument+Sans:wght@400;500;600;700&display=swap');
        *{box-sizing:border-box;margin:0}
        ::-webkit-scrollbar{width:6px}
        ::-webkit-scrollbar-track{background:transparent}
        ::-webkit-scrollbar-thumb{background:${C.border};border-radius:3px}
        html{-webkit-text-size-adjust:100%}
        input,button{font-size:16px}
        @media(min-width:641px){input,button{font-size:inherit}}
        @keyframes fadeUp{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:translateY(0)}}
        .page-in{animation:fadeUp 0.25s ease-out}
        @keyframes typingPulse{0%,100%{opacity:0.3}50%{opacity:1}}
        .typing-dot{animation:typingPulse 1s ease-in-out infinite}
      `}</style>
      <div style={{ position: "fixed", width: 700, height: 700, top: -250, left: -150, background: `radial-gradient(circle, ${C.purple} 0%, transparent 70%)`, opacity: 0.06, borderRadius: "50%", pointerEvents: "none", zIndex: 0, filter: "blur(80px)" }} />
      <div style={{ position: "fixed", width: 500, height: 500, bottom: -200, right: -100, background: `radial-gradient(circle, ${C.accent} 0%, transparent 70%)`, opacity: 0.04, borderRadius: "50%", pointerEvents: "none", zIndex: 0, filter: "blur(60px)" }} />
      <div style={{ position: "relative", zIndex: 1, paddingBottom: mob ? 80 : 0 }}>
        <Nav active={active} setActive={setActive} />
        <div key={active} className="page-in">
          {active === "landing" && <LandingPage setActive={setActive} />}
          {active === "board" && <BoardPage />}
          {active === "insights" && <InsightsPage />}
          {active === "wrapped" && <WrappedPage />}
          {active === "coach" && <CoachPage />}
        </div>
      </div>
    </div>
  );
}
